import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";

import Colors from "../../../Resources/Color";
import { useDevice } from "../../_layout";
import {
  getCenteredTabScrollOffset,
  getResponsiveTabGap,
} from "./tabBarScrollUtils";

const INDICATOR_ANIMATION_DURATION_MS = 180;

const UnifiedTabBar = memo(function UnifiedTabBar({
  tabs = [],
  selectedKey,
  onSelect,
  topMargin = 24,
  itemGap = 24,
  activeColor = Colors.bubbleColor,
  inactiveColor = "black",
  borderColor = Colors.borderColor,
  autoScrollToActive = true,
}) {
  const { isMobile } = useDevice();
  const { width: windowWidth } = useWindowDimensions();
  const scrollViewRef = useRef(null);
  const indicatorX = useRef(new Animated.Value(0)).current;
  const indicatorWidth = useRef(new Animated.Value(0)).current;

  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [indicatorReady, setIndicatorReady] = useState(false);
  const [tabLayouts, setTabLayouts] = useState({});
  const [labelWidths, setLabelWidths] = useState({});
  const [layoutEpoch, setLayoutEpoch] = useState(0);
  const screenBucket = useMemo(() => {
    if (windowWidth < 600) return "small";
    if (windowWidth < 1090) return "medium";
    return "large";
  }, [windowWidth]);
  const refreshMeasurements = useCallback(() => {
    setIndicatorReady(false);
    setHoveredKey(null);
    setTabLayouts({});
    setLabelWidths({});
    setContentWidth(0);
    setLayoutEpoch((previous) => previous + 1);
  }, []);
  const tabsMeasurementSignature = useMemo(
    () =>
      tabs
        .map((tab) => `${String(tab.key)}::${String(tab.label)}::${tab.showDot ? "1" : "0"}`)
        .join("||"),
    [tabs]
  );

  const selectedLayout = tabLayouts[selectedKey];
  const selectedLabelWidth = labelWidths[selectedKey];
  const measuredTabCount = useMemo(
    () => tabs.reduce((count, tab) => count + (tabLayouts[tab.key] ? 1 : 0), 0),
    [tabLayouts, tabs]
  );
  const tabWidthsTotal = useMemo(
    () =>
      tabs.reduce((total, tab) => {
        const width = tabLayouts[tab.key]?.width;
        return total + (typeof width === "number" ? width : 0);
      }, 0),
    [tabLayouts, tabs]
  );
  const resolvedItemGap = useMemo(
    () =>
      getResponsiveTabGap({
        preferredGap: itemGap,
        screenWidth: windowWidth,
        containerWidth,
        tabWidthsTotal,
        tabCount: tabs.length,
        measuredTabCount,
      }),
    [containerWidth, itemGap, measuredTabCount, tabWidthsTotal, tabs.length, windowWidth]
  );

  const indicatorTarget = useMemo(() => {
    if (!selectedLayout) return null;

    const width =
      typeof selectedLabelWidth === "number" && selectedLabelWidth > 0
        ? selectedLabelWidth
        : selectedLayout.width;
    const x = selectedLayout.x + (selectedLayout.width - width) / 2;

    return { x, width };
  }, [selectedLayout, selectedLabelWidth]);

  const updateTabLayout = useCallback((key, { nativeEvent }) => {
    const { x, width } = nativeEvent.layout;

    setTabLayouts((prev) => {
      const current = prev[key];
      if (current && current.x === x && current.width === width) {
        return prev;
      }

      return {
        ...prev,
        [key]: { x, width },
      };
    });
  }, []);

  const updateLabelWidth = useCallback((key, { nativeEvent }) => {
    const width = nativeEvent.layout?.width;
    if (typeof width !== "number" || width <= 0) return;

    setLabelWidths((prev) => {
      if (prev[key] === width) return prev;
      return {
        ...prev,
        [key]: width,
      };
    });
  }, []);

  const handleTabPress = useCallback(
    (key) => {
      if (!key || key === selectedKey) return;
      onSelect?.(key);
    },
    [onSelect, selectedKey]
  );

  useEffect(() => {
    if (!indicatorTarget) return;

    if (!indicatorReady) {
      indicatorX.setValue(indicatorTarget.x);
      indicatorWidth.setValue(indicatorTarget.width);
      setIndicatorReady(true);
      return;
    }

    Animated.parallel([
      Animated.timing(indicatorX, {
        toValue: indicatorTarget.x,
        duration: INDICATOR_ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(indicatorWidth, {
        toValue: indicatorTarget.width,
        duration: INDICATOR_ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }),
    ]).start();
  }, [indicatorReady, indicatorTarget, indicatorWidth, indicatorX]);

  useEffect(() => {
    if (!autoScrollToActive || !selectedLayout || !scrollViewRef.current) return;

    const targetX = getCenteredTabScrollOffset({
      tabX: selectedLayout.x,
      tabWidth: selectedLayout.width,
      containerWidth,
      contentWidth,
    });

    scrollViewRef.current.scrollTo({ x: targetX, animated: true });
  }, [autoScrollToActive, containerWidth, contentWidth, selectedLayout, selectedKey]);

  useEffect(() => {
    refreshMeasurements();
  }, [screenBucket, tabsMeasurementSignature, refreshMeasurements]);

  return (
    <>
      <View onLayout={({ nativeEvent }) => setContainerWidth(nativeEvent.layout.width)}>
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          onContentSizeChange={(width) => setContentWidth(width)}
          contentContainerStyle={[styles.contentContainer, { marginTop: topMargin }]}
        >
          <View style={styles.row}>
            {tabs.map((tab, index) => {
              const isActive = selectedKey === tab.key;
              const isHovered = hoveredKey === tab.key;
              const tabRenderKey = `${layoutEpoch}:${tab.key}:${tab.label}:${tab.showDot ? "1" : "0"}`;

              return (
                <View
                  key={tabRenderKey}
                  onLayout={(event) => updateTabLayout(tab.key, event)}
                  style={[
                    styles.tabItem,
                    { marginRight: index < tabs.length - 1 ? resolvedItemGap : 0 },
                  ]}
                >
                  <TouchableOpacity
                    activeOpacity={0.75}
                    onPress={() => handleTabPress(tab.key)}
                    onMouseEnter={!isMobile ? () => setHoveredKey(tab.key) : undefined}
                    onMouseLeave={!isMobile ? () => setHoveredKey(null) : undefined}
                  >
                    <View style={styles.tabContent}>
                      <View style={styles.labelRow}>
                        <Text
                          onLayout={(event) => updateLabelWidth(tab.key, event)}
                          style={[
                            styles.tabText,
                            {
                              color: isActive || isHovered ? activeColor : inactiveColor,
                            },
                          ]}
                        >
                          {tab.label}
                        </Text>
                        {tab.showDot ? <View style={[styles.dot, { backgroundColor: activeColor }]} /> : null}
                      </View>
                    </View>
                  </TouchableOpacity>
                </View>
              );
            })}
            <Animated.View
              pointerEvents="none"
              style={[
                styles.activeUnderline,
                {
                  opacity: indicatorReady ? 1 : 0,
                  backgroundColor: activeColor,
                  transform: [{ translateX: indicatorX }],
                  width: indicatorWidth,
                },
              ]}
            />
          </View>
        </ScrollView>
      </View>
      <View style={[styles.fullWidthBaseline, { backgroundColor: borderColor }]} />
    </>
  );
});

const styles = StyleSheet.create({
  contentContainer: {
    paddingBottom: 0,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
    paddingBottom: 0,
  },
  tabItem: {
    justifyContent: "center",
    paddingBottom: 7,
  },
  tabContent: {
    alignItems: "center",
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "400",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginLeft: 6,
  },
  activeUnderline: {
    position: "absolute",
    bottom: 0,
    left: 0,
    height: 2,
    borderRadius: 1,
  },
  fullWidthBaseline: {
    height: 1,
    width: "100%",
  },
});

export default UnifiedTabBar;
